import React, { useState, useEffect } from 'react';
import { Printer, CheckCircle, XCircle, ChevronDown, ChevronUp, Clock, List, LayoutGrid, Server } from 'lucide-react';
import { PrinterDto } from '../../../services/api';
import { printingApi } from '../../../services/api';
import { PrinterStatusBadge } from './PrinterStatusBadge';
import { AgentStatusIndicator } from './AgentStatusIndicator';
import { PrinterFilters } from './PrinterFilters';
import { PrintJobsModal } from './PrintJobsModal';
import { PrinterCard } from './PrinterCard';
import { AgentGroupView } from './AgentGroupView';

export const PrintersList: React.FC = () => {
  const [printers, setPrinters] = useState<PrinterDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'unavailable' | 'error'>('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedPrinter, setSelectedPrinter] = useState<{ id: string; name: string } | null>(null);
  const [viewMode, setViewMode] = useState<'printers' | 'agents'>('printers');

  useEffect(() => {
    fetchPrinters();
  }, []);

  const fetchPrinters = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await printingApi.getPrinters();
      setPrinters(data);
    } catch (err: any) {
      console.error('Error fetching printers:', err);
      setError(err.message || 'Error al cargar las impresoras');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRow = (printerId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(printerId)) {
      newExpanded.delete(printerId);
    } else {
      newExpanded.add(printerId);
    }
    setExpandedRows(newExpanded);
  };

  const filteredPrinters = printers.filter((printer) => {
    // Filter by search term
    const matchesSearch = !searchTerm ||
      printer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      printer.location?.toLowerCase().includes(searchTerm.toLowerCase());

    // Filter by status
    const matchesStatus = statusFilter === 'all' || printer.overallStatus === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const formatLastSeen = (lastSeen: string) => {
    try {
      const date = new Date(lastSeen);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);

      if (diffMins < 1) return 'Ahora mismo';
      if (diffMins < 60) return `Hace ${diffMins} min`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `Hace ${diffHours}h`;
      const diffDays = Math.floor(diffHours / 24);
      return `Hace ${diffDays}d`;
    } catch {
      return lastSeen;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mb-4"></div>
          <p className="text-slate-600">Cargando impresoras...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Printer className="mx-auto mb-4 text-red-500" size={48} />
          <p className="text-red-600 font-medium mb-2">Error al cargar las impresoras</p>
          <p className="text-slate-600 text-sm">{error}</p>
          <button
            onClick={fetchPrinters}
            className="mt-4 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <PrinterFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        totalCount={printers.length}
        filteredCount={filteredPrinters.length}
      />

      {/* View Toggle (Adapting to be visible always if needed, or kept mobile only, but user asked for DESKTOP design equal to MOBILE) */}
      {/* Keeping toggle visible for all screens to allow switching between Printers and Agents views */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <button
          onClick={() => setViewMode('printers')}
          className={`flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
            viewMode === 'printers'
              ? 'bg-green-600 border-green-600 text-white shadow-lg shadow-green-200'
              : 'bg-white border-slate-200 text-slate-600 hover:border-green-200 hover:bg-green-50'
          }`}
        >
          <LayoutGrid size={24} />
          <span className="font-bold text-lg">Impresoras</span>
        </button>
        <button
          onClick={() => setViewMode('agents')}
          className={`flex items-center justify-center gap-2 p-4 rounded-xl border-2 transition-all ${
            viewMode === 'agents'
              ? 'bg-green-600 border-green-600 text-white shadow-lg shadow-green-200'
              : 'bg-white border-slate-200 text-slate-600 hover:border-green-200 hover:bg-green-50'
          }`}
        >
          <Server size={24} />
          <span className="font-bold text-lg">Agentes</span>
        </button>
      </div>

      {/* Main Content (Universal View) */}
      <div>
        {filteredPrinters.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400 bg-white rounded-xl border border-slate-200">
            <Printer className="h-12 w-12 mb-3" />
            <p className="text-lg font-medium">No se encontraron resultados</p>
            <p className="text-sm mt-2">
               {printers.length === 0
                 ? 'No se encontraron impresoras disponibles en el sistema'
                 : 'Intenta ajustar los filtros de búsqueda'}
             </p>
          </div>
        ) : viewMode === 'printers' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredPrinters.map((printer) => (
              <PrinterCard
                key={printer.id}
                printer={printer}
                onViewQueue={(id, name) => setSelectedPrinter({ id, name })}
              />
            ))}
          </div>
        ) : (
          <AgentGroupView 
            printers={filteredPrinters}
            onViewQueue={(id, name) => setSelectedPrinter({ id, name })}
          />
        )}
      </div>

      {/* Print Jobs Modal */}
      {selectedPrinter && (
        <PrintJobsModal
          printerId={selectedPrinter.id}
          printerName={selectedPrinter.name}
          isOpen={!!selectedPrinter}
          onClose={() => setSelectedPrinter(null)}
        />
      )}
    </div>
  );
};
